import { useEffect, useState, useRef, useMemo } from 'react';
import close_icon from '../../assets/images/close_icon.svg';
import { saveUserInfoArbitrage } from '../../utils/api/saveUserInfo';
import { LEAGUES, MARKETS, SPORTS, SPORTSBOOKS, getWagerWizDisplayOptions, convertWagerWizSelectionToBackend, convertBackendToWagerWizDisplay } from '../../utils/constants';
import { CustomAutoCompleteFilter } from '../evscreen/Filters/CustomAutocompleteFilter';
import { DatePickerComponent } from '../evscreen/Filters/Datepicker';
import { IOSSwitch } from '../../components/IOSSwitch';
import { Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import checked_checkbox from '../../assets/images/checked_checkbox.svg';
import unchecked_checkbox from '../../assets/images/unchecked_checkbox.svg';
import magnifying_glass from '../../assets/images/magnifying_glass.svg';
import DatePicker from 'react-datepicker';
import { LEAGUE_SPORT_MAP } from '../../pages/screener/leagueToSportMapping';

// Sportsbooks Modal Content Component - Direct checklist interface
const SportsbooksModalContent = ({ filters, index, setFilters, email, saveUserInfo, defaultStake }) => {
	const [searchTerm, setSearchTerm] = useState('');
	
	const allOptions = SPORTSBOOKS;
	const currentValue = filters[index]?.sportsbooks || [];
	const filteredOptions = allOptions.filter(option => 
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleToggle = async (option) => {
		const copy = JSON.parse(JSON.stringify(filters));
		const currentOptions = copy[index]?.sportsbooks || [];
		const isSelected = currentOptions.some(item => item.value === option.value);
		
		let newOptions;
		if (isSelected) {
			newOptions = currentOptions.filter(item => item.value !== option.value);
		} else {
			newOptions = [...currentOptions, option];
		}
		
		copy[index].sportsbooks = newOptions;
		setFilters(copy);
		await saveUserInfo(copy, email, false, defaultStake);
	};


	const isChecked = (option) => {
		return currentValue.some(item => item.value === option.value);
	};

	return (
		<div className="space-y-4">
			{/* Search Bar */}
			<div className="flex flex-row items-center gap-2 border border-[#363844] rounded-lg px-3 py-2 bg-[#17191d]">
				<img src={magnifying_glass} alt="search" className="w-4 h-4 opacity-70" />
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					placeholder="Search for a sportsbook"
					className="flex-1 bg-transparent border-none outline-none text-sm text-[#eff1f5] placeholder-gray-500"
				/>
			</div>

			{/* Options List */}
			<div className="max-h-96 overflow-y-auto space-y-2">
				{filteredOptions.map((option) => (
					<div
						key={option.value}
						onClick={() => handleToggle(option)}
						className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#22262e] transition-colors"
					>
						<img 
							src={isChecked(option) ? checked_checkbox : unchecked_checkbox} 
							alt={isChecked(option) ? 'checked' : 'unchecked'}
							className="w-5 h-5 flex-shrink-0"
						/>
						<span className={`text-sm ${isChecked(option) ? 'text-[#eff1f5] font-medium' : 'text-gray-400'}`}>
							{option.label}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

// Sports Modal Content Component - Direct checklist interface
const SportsModalContent = ({ filters, index, setFilters, email, saveUserInfo, defaultStake }) => {
	const [searchTerm, setSearchTerm] = useState('');
	
	const allOptions = SPORTS;
	const currentValue = filters[index]?.sports || [];
	const filteredOptions = allOptions.filter(option => 
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleToggle = async (option) => {
		const copy = JSON.parse(JSON.stringify(filters));
		const currentOptions = copy[index]?.sports || [];
		const isSelected = currentOptions.some(item => item.value === option.value);
		
		let newOptions;
		if (isSelected) {
			newOptions = currentOptions.filter(item => item.value !== option.value);
		} else {
			newOptions = [...currentOptions, option];
		}
		
		copy[index].sports = newOptions;
		setFilters(copy);
		await saveUserInfo(copy, email, false, defaultStake);
	};


	const isChecked = (option) => {
		return currentValue.some(item => item.value === option.value);
	};

	return (
		<div className="space-y-4">
			{/* Search Bar */}
			<div className="flex flex-row items-center gap-2 border border-[#363844] rounded-lg px-3 py-2 bg-[#17191d]">
				<img src={magnifying_glass} alt="search" className="w-4 h-4 opacity-70" />
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					placeholder="Search for a sport"
					className="flex-1 bg-transparent border-none outline-none text-sm text-[#eff1f5] placeholder-gray-500"
				/>
			</div>

			{/* Options List */}
			<div className="max-h-96 overflow-y-auto space-y-2">
				{filteredOptions.map((option) => (
					<div
						key={option.value}
						onClick={() => handleToggle(option)}
						className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#22262e] transition-colors"
					>
						<img 
							src={isChecked(option) ? checked_checkbox : unchecked_checkbox} 
							alt={isChecked(option) ? 'checked' : 'unchecked'}
							className="w-5 h-5 flex-shrink-0"
						/>
						<span className={`text-sm ${isChecked(option) ? 'text-[#eff1f5] font-medium' : 'text-gray-400'}`}>
							{option.label.charAt(0).toUpperCase() + option.label.slice(1)}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

// Market Types Modal Content Component - Direct checklist interface
const MarketTypesModalContent = ({ filters, index, setFilters, email, saveUserInfo, defaultStake }) => {
	const [searchTerm, setSearchTerm] = useState('');
	
	const allOptions = [
		{ value: 'Main Markets', label: 'Main Markets' },
		{ value: 'Alternate Markets', label: 'Alternate Markets' },
		{ value: 'Player Props', label: 'Player Props' },
	];
	const currentValue = filters[index]?.marketTypes || [];
	const filteredOptions = allOptions.filter(option => 
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleToggle = async (option) => {
		const copy = JSON.parse(JSON.stringify(filters));
		const currentOptions = copy[index]?.marketTypes || [];
		const isSelected = currentOptions.some(item => item.value === option.value);
		
		let newOptions;
		if (isSelected) {
			newOptions = currentOptions.filter(item => item.value !== option.value);
		} else {
			newOptions = [...currentOptions, option];
		}
		
		copy[index].marketTypes = newOptions;
		setFilters(copy);
		await saveUserInfo(copy, email, false, defaultStake);
	};


	const isChecked = (option) => {
		return currentValue.some(item => item.value === option.value);
	};

	return (
		<div className="space-y-4">
			{/* Search Bar */}
			<div className="flex flex-row items-center gap-2 border border-[#363844] rounded-lg px-3 py-2 bg-[#17191d]">
				<img src={magnifying_glass} alt="search" className="w-4 h-4 opacity-70" />
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					placeholder="Search for a market type"
					className="flex-1 bg-transparent border-none outline-none text-sm text-[#eff1f5] placeholder-gray-500"
				/>
			</div>

			{/* Options List */}
			<div className="max-h-96 overflow-y-auto space-y-2">
				{filteredOptions.map((option) => (
					<div
						key={option.value}
						onClick={() => handleToggle(option)}
						className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#22262e] transition-colors"
					>
						<img 
							src={isChecked(option) ? checked_checkbox : unchecked_checkbox} 
							alt={isChecked(option) ? 'checked' : 'unchecked'}
							className="w-5 h-5 flex-shrink-0"
						/>
						<span className={`text-sm ${isChecked(option) ? 'text-[#eff1f5] font-medium' : 'text-gray-400'}`}>
							{option.label}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

// Date Modal Content Component - Direct date picker interface
const DateModalContent = ({ filters, index, setFilters, email, saveUserInfo, defaultStake }) => {
	function getDateDifferenceInDays(targetDate) {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		targetDate.setHours(0, 0, 0, 0);
		const msPerDay = 1000 * 60 * 60 * 24;
		const diffInMs = targetDate - today;
		const diffInDays = Math.round(diffInMs / msPerDay);
		return diffInDays;
	}

	let displayDate = new Date();
	if (filters?.[index]?.dateDifference !== undefined && filters?.[index]?.dateDifference !== null) {
		if (filters[index].dateDifference === 0) {
			displayDate = new Date();
			displayDate.setHours(0, 0, 0, 0);
		} else {
			const currentTime = Date.now();
			const futureTime = currentTime + filters[index].dateDifference * 24 * 60 * 60 * 1000;
			displayDate = new Date(futureTime);
			displayDate.setHours(0, 0, 0, 0);
		}
	} else {
		displayDate.setHours(0, 0, 0, 0);
	}

	const handleDateChange = async (newDate) => {
		try {
			const copy = JSON.parse(JSON.stringify(filters));
			const normalizedDate = new Date(newDate);
			normalizedDate.setHours(0, 0, 0, 0);
			copy[index].date = normalizedDate.toString();
			copy[index].dateDifference = getDateDifferenceInDays(normalizedDate);
			setFilters(copy);
			await saveUserInfo(copy, email, false, defaultStake);
		} catch (err) {
			console.error('Error updating date:', err);
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-col items-center">
				<div className="w-full max-w-xs datepicker-wrapper-modal" style={{ overflow: 'visible' }}>
					<DatePicker
						selected={displayDate}
						onChange={(date) => handleDateChange(date)}
						minDate={new Date()}
						inline
						className="w-full"
						wrapperClassName="w-full"
						strictParsing
						disabledKeyboardNavigation
					/>
				</div>
			</div>
			<div className="flex items-center justify-center gap-4 pt-4 border-t border-[#363844]">
				<div className="text-sm text-gray-400">Selected:</div>
				<div className="text-sm font-medium text-[#eff1f5]">
					{displayDate.toLocaleDateString('en-US', { 
						weekday: 'long',
						month: 'long', 
						day: 'numeric', 
						year: 'numeric' 
					})}
				</div>
			</div>
		</div>
	);
};

// Leagues Modal Content Component - Direct checklist interface with sport categorization
const LeaguesModalContent = ({ filters, index, setFilters, email, saveUserInfo, defaultStake }) => {
	const [searchTerm, setSearchTerm] = useState('');
	
	const currentValue = filters[index]?.leagues || [];
	
	// Custom mapping for corrections and popular category
	const customLeagueSportMap = {
		// Popular category
		'NBA': 'popular',
		'NHL': 'popular',
		'NFL': 'popular',
		'MLB': 'popular',
		'NCAAB': 'popular',
		'NCAAF': 'popular',
		// Tennis
		'ATP': 'tennis',
		'ATP Challenger': 'tennis',
		'WTA': 'tennis',
		// Soccer
		'Belarus - Premier League': 'soccer',
		'Spain - La Liga': 'soccer',
		'Spain - La Liga 2': 'soccer',
		// Football
		'CFL': 'football',
		// Esports
		'CS2': 'esports',
		'Dota 2': 'esports',
		'League of Legends': 'esports',
		'Rocket League': 'esports',
		'Valorant': 'esports',
		// Hockey
		'NCAAH': 'hockey',
		// Golf
		'PGA': 'golf',
		// MMA
		'UFC': 'mma',
		// Tennis (International Tours)
		'International - Tours': 'tennis',
		'International - Tours Women': 'tennis',
	};
	
	// Group leagues by sport using custom map first, then fallback to LEAGUE_SPORT_MAP
	const groupedBySport = LEAGUES.reduce((acc, league) => {
		const sport = customLeagueSportMap[league.value] || LEAGUE_SPORT_MAP[league.value] || 'other';
		if (!acc[sport]) {
			acc[sport] = [];
		}
		acc[sport].push(league);
		return acc;
	}, {});
	
	// Get sport display names
	const getSportDisplayName = (sport) => {
		if (sport === 'popular') return 'Popular';
		const sportName = SPORTS.find(s => s.value === sport);
		return sportName ? sportName.label.charAt(0).toUpperCase() + sportName.label.slice(1) : sport.charAt(0).toUpperCase() + sport.slice(1);
	};
	
	// Filter leagues and groups based on search term
	const filteredGroups = Object.keys(groupedBySport).reduce((acc, sport) => {
		const filteredLeagues = groupedBySport[sport].filter(league =>
			league.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
			sport.toLowerCase().includes(searchTerm.toLowerCase())
		);
		if (filteredLeagues.length > 0) {
			acc[sport] = filteredLeagues;
		}
		return acc;
	}, {});
	
	// Get all filtered leagues for Select All
	const allFilteredLeagues = Object.values(filteredGroups).flat();
	
	const handleToggle = async (option) => {
		const copy = JSON.parse(JSON.stringify(filters));
		const currentOptions = copy[index]?.leagues || [];
		const isSelected = currentOptions.some(item => item.value === option.value);
		
		let newOptions;
		if (isSelected) {
			newOptions = currentOptions.filter(item => item.value !== option.value);
		} else {
			newOptions = [...currentOptions, option];
		}
		
		copy[index].leagues = newOptions;
		setFilters(copy);
		await saveUserInfo(copy, email, false, defaultStake);
	};
	
	
	const isChecked = (option) => {
		return currentValue.some(item => item.value === option.value);
	};
	
	// Sort sports for display - Popular first, then alphabetically, then Other last
	const sortedSports = Object.keys(filteredGroups).sort((a, b) => {
		if (a === 'popular') return -1;
		if (b === 'popular') return 1;
		if (a === 'other') return 1;
		if (b === 'other') return -1;
		return a.localeCompare(b);
	});
	
	return (
		<div className="space-y-4">
			{/* Search Bar */}
			<div className="flex flex-row items-center gap-2 border border-[#363844] rounded-lg px-3 py-2 bg-[#17191d]">
				<img src={magnifying_glass} alt="search" className="w-4 h-4 opacity-70" />
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					placeholder="Search for a league"
					className="flex-1 bg-transparent border-none outline-none text-sm text-[#eff1f5] placeholder-gray-500"
				/>
			</div>
			
			{/* Options List Grouped by Sport */}
			<div className="max-h-96 overflow-y-auto space-y-4">
				{sortedSports.map((sport) => (
					<div key={sport} className="space-y-2">
						{/* Sport Header */}
						<div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2">
							{getSportDisplayName(sport)}
						</div>
						{/* Leagues in this sport */}
						<div className="space-y-2">
							{filteredGroups[sport].map((option) => (
								<div
									key={option.value}
									onClick={() => handleToggle(option)}
									className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#22262e] transition-colors ml-2"
								>
									<img 
										src={isChecked(option) ? checked_checkbox : unchecked_checkbox} 
										alt={isChecked(option) ? 'checked' : 'unchecked'}
										className="w-5 h-5 flex-shrink-0"
									/>
									<span className={`text-sm ${isChecked(option) ? 'text-[#eff1f5] font-medium' : 'text-gray-400'}`}>
										{option.label}
									</span>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// Helper function to categorize markets by Type and Time
const categorizeMarkets = (markets) => {
	const categorized = {};
	const seen = new Set(); // Track unique markets
	
	markets.forEach(market => {
		// Skip duplicates
		if (seen.has(market)) return;
		seen.add(market);
		
		// Determine Type
		let type = 'Other';
		const marketLower = market.toLowerCase();
		
		if (marketLower.includes('player') || marketLower.includes('fantasy score') || marketLower.includes('scorer') || 
			marketLower.includes('batsman') || marketLower.includes('bowler') || marketLower.includes('man of the match') ||
			marketLower.includes('first goal scorer') || marketLower.includes('last goal scorer') || 
			marketLower.includes('second goal scorer') || marketLower.includes('third goal scorer') ||
			marketLower.includes('first touchdown scorer') || marketLower.includes('last touchdown scorer') ||
			marketLower.includes('first basket') || marketLower.includes('first try scorer') ||
			marketLower.includes('first card receiver') || marketLower.includes('anytime card receiver') ||
			marketLower.includes('anytime red card receiver') || marketLower.includes('first card') ||
			marketLower.includes('player assists') || marketLower.includes('player goals') ||
			marketLower.includes('player points') || marketLower.includes('player rebounds') ||
			marketLower.includes('player saves') || marketLower.includes('player shots') ||
			marketLower.includes('player passes') || marketLower.includes('player tackles') ||
			marketLower.includes('player blocks') || marketLower.includes('player steals') ||
			marketLower.includes('player runs') || marketLower.includes('player hits') ||
			marketLower.includes('player strikeouts') || marketLower.includes('player aces') ||
			marketLower.includes('player cards') || marketLower.includes('player fouls') ||
			marketLower.includes('player interceptions') || marketLower.includes('player sacks') ||
			marketLower.includes('player touchdowns') || marketLower.includes('player yards') ||
			marketLower.includes('player fantasy')) {
			type = 'Player Prop';
		} else if (marketLower.includes('moneyline') || marketLower.includes('draw no bet')) {
			type = 'Moneyline';
		} else if (marketLower.includes('spread') || marketLower.includes('handicap') || marketLower.includes('puck line') || 
			marketLower.includes('run line') || marketLower.includes('point spread') || marketLower.includes('goal spread') ||
			marketLower.includes('asian handicap') || marketLower.includes('game spread') || 
			marketLower.includes('set handicap') || marketLower.includes('round handicap') ||
			marketLower.includes('map handicap') || marketLower.includes('kills handicap') ||
			marketLower.includes('aces handicap') || marketLower.includes('cards handicap') ||
			marketLower.includes('corners handicap')) {
			type = 'Spread';
		} else if (marketLower.includes('total') || marketLower.includes('over/under') || marketLower.includes('odd/even')) {
			type = 'Total';
		} else if (marketLower.includes('team total') || marketLower.includes('team to score') || 
			marketLower.includes('team to win') || marketLower.includes('team clean sheet') ||
			marketLower.includes('first team to score') || marketLower.includes('last team to score') ||
			marketLower.includes('team highest') || marketLower.includes('team with most') ||
			marketLower.includes('team top')) {
			type = 'Team';
		} else if (marketLower.includes('race to')) {
			type = 'Race To';
		} else {
			type = 'Other';
		}
		
		// Determine Time - Group innings, maps, and sets together
		let time = 'Game';
		
		// Check for innings first (group all innings under "Inning")
		if (marketLower.includes('inning') || marketLower.includes('innings')) {
			time = 'Inning';
		} 
		// Check for sets (group all sets under "Set")
		else if (/[0-9]+(st|nd|rd|th)\s+set/i.test(marketLower) || 
			(marketLower.includes('set') && !marketLower.includes('handicap') && !marketLower.includes('set handicap') &&
			 (marketLower.includes('1st set') || marketLower.includes('2nd set') || 
			  marketLower.includes('3rd set') || marketLower.includes('4th set') || 
			  marketLower.includes('5th set')))) {
			time = 'Set';
		}
		// Check for maps (group all maps under "Map")
		else if (/[0-9]+(st|nd|rd|th)\s+([0-9]+\s+)?maps?/i.test(marketLower) || 
			(marketLower.includes('map') && !marketLower.includes('handicap') && !marketLower.includes('map handicap') &&
			 (marketLower.includes('1st map') || marketLower.includes('2nd map') || 
			  marketLower.includes('3rd map') || marketLower.includes('4th map') || 
			  marketLower.includes('5th map') || marketLower.includes('1st 2 maps') ||
			  marketLower.includes('1st 3 maps') || marketLower.includes('1st 4 maps') ||
			  marketLower.includes('maps player') || /[0-9]+(st|nd|rd|th)\s+[0-9]+\s+maps/i.test(marketLower)))) {
			time = 'Map';
		}
		// Check for other time periods
		else if (marketLower.includes('1st half') || marketLower.includes('1st quarter') || 
			marketLower.includes('1st period') ||
			marketLower.includes('1st 10 minutes') || marketLower.includes('1st 5 minutes') ||
			marketLower.includes('1st 3 quarters') ||
			marketLower.includes('1st round') || marketLower.includes('1st 3 holes') ||
			marketLower.includes('1st x overs')) {
			if (marketLower.includes('1st half')) time = '1st Half';
			else if (marketLower.includes('1st quarter')) time = '1st Quarter';
			else if (marketLower.includes('1st period')) time = '1st Period';
			else if (marketLower.includes('1st 10 minutes')) time = '1st 10 Mins';
			else if (marketLower.includes('1st 5 minutes')) time = '1st 5 Mins';
			else if (marketLower.includes('1st 3 quarters')) time = '1st 3 Quarter';
			else if (marketLower.includes('1st round')) time = '1st Round';
			else if (marketLower.includes('1st 3 holes')) time = '1st 3 Holes';
			else if (marketLower.includes('1st x overs')) time = '1st X Overs';
			else time = '1st Half';
		} else if (marketLower.includes('2nd half') || marketLower.includes('2nd quarter') || 
			marketLower.includes('2nd period') ||
			marketLower.includes('2nd round')) {
			if (marketLower.includes('2nd half')) time = '2nd Half';
			else if (marketLower.includes('2nd quarter')) time = '2nd Quarter';
			else if (marketLower.includes('2nd period')) time = '2nd Period';
			else if (marketLower.includes('2nd round')) time = '2nd Round';
			else time = '2nd Half';
		} else if (marketLower.includes('3rd quarter') || marketLower.includes('3rd period')) {
			if (marketLower.includes('3rd quarter')) time = '3rd Quarter';
			else if (marketLower.includes('3rd period')) time = '3rd Period';
			else time = '3rd Quarter';
		} else if (marketLower.includes('4th quarter')) {
			time = '4th Quarter';
		} else if (marketLower.includes('reg time') || marketLower.includes('no ot') || marketLower.includes('(no ot)')) {
			time = 'Game';
		} else if (marketLower.includes('single over') || marketLower.includes('1st over') || 
			marketLower.includes('2nd over') || marketLower.includes('over team total')) {
			time = 'Single Over';
		} else if (marketLower.includes('1st 3 quarter')) {
			time = '1st 3 Quarter';
		} else if (marketLower.includes('golf')) {
			time = 'Golf';
		} else {
			time = 'Game';
		}
		
		// Add to categorized structure
		if (!categorized[type]) {
			categorized[type] = {};
		}
		if (!categorized[type][time]) {
			categorized[type][time] = [];
		}
		categorized[type][time].push({ label: market, value: market });
	});
	
	return categorized;
};

// Markets Modal Content Component - Categorized by Type and Time
const MarketsModalContent = ({ filters, index, setFilters, email, saveUserInfo, defaultStake }) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [expandedTypes, setExpandedTypes] = useState({});
	const [expandedTimes, setExpandedTimes] = useState({});
	
	const allOptions = MARKETS.map((m) => ({ label: m, value: m }));
	const currentValue = filters[index]?.markets || [];
	const categorizedMarkets = useMemo(() => categorizeMarkets(MARKETS), []);
	
	// Flatten for search filtering
	const filteredOptions = allOptions.filter(option => 
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);
	
	// Filter categorized structure by search term
	const filteredCategorized = useMemo(() => {
		if (!searchTerm) return categorizedMarkets;
		
		const filtered = {};
		Object.keys(categorizedMarkets).forEach(type => {
			filtered[type] = {};
			Object.keys(categorizedMarkets[type]).forEach(time => {
				const markets = categorizedMarkets[type][time].filter(m => 
					m.label.toLowerCase().includes(searchTerm.toLowerCase())
				);
				if (markets.length > 0) {
					filtered[type][time] = markets;
				}
			});
			if (Object.keys(filtered[type]).length === 0) {
				delete filtered[type];
			}
		});
		return filtered;
	}, [searchTerm, categorizedMarkets]);
	
	const handleToggle = async (option) => {
		const copy = JSON.parse(JSON.stringify(filters));
		const currentOptions = copy[index]?.markets || [];
		const isSelected = currentOptions.some(item => item.value === option.value);
		
		let newOptions;
		if (isSelected) {
			newOptions = currentOptions.filter(item => item.value !== option.value);
		} else {
			newOptions = [...currentOptions, option];
		}
		
		copy[index].markets = newOptions;
		setFilters(copy);
		await saveUserInfo(copy, email, false, defaultStake);
	};
	
	
	const isChecked = (option) => {
		return currentValue.some(item => item.value === option.value);
	};
	
	const toggleType = (type) => {
		setExpandedTypes(prev => {
			const currentValue = prev[type];
			return {
				...prev,
				[type]: currentValue === true ? false : true
			};
		});
	};
	
	const toggleTime = (typeTime) => {
		setExpandedTimes(prev => {
			const currentValue = prev[typeTime];
			return {
				...prev,
				[typeTime]: currentValue === true ? false : true
			};
		});
	};
	
	// Sort types and times
	const typeOrder = ['Moneyline', 'Spread', 'Total', 'Team', 'Player Prop', 'Race To', 'Other'];
	const sortedTypes = Object.keys(filteredCategorized).sort((a, b) => {
		const indexA = typeOrder.indexOf(a);
		const indexB = typeOrder.indexOf(b);
		if (indexA === -1 && indexB === -1) return a.localeCompare(b);
		if (indexA === -1) return 1;
		if (indexB === -1) return -1;
		return indexA - indexB;
	});
	
	// Prioritize: Game first, then Half, then Quarter, then Period, then others
	const timeOrder = [
		'Game',
		'1st Half', '2nd Half',
		'1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter', '1st 3 Quarter',
		'1st Period', '2nd Period', '3rd Period',
		'1st 10 Mins', '1st 5 Mins', 'Set', 'Inning', 'Map',
		'Golf', 'Single Over', '1st X Overs', '1st 2 innings', '1st 4 innings', '1st 7 innings', '1st 6 innings', '1st Round', '1st 3 Holes', '2nd Round'
	];
	
	const sortTimes = (times) => {
		return times.sort((a, b) => {
			const indexA = timeOrder.indexOf(a);
			const indexB = timeOrder.indexOf(b);
			if (indexA === -1 && indexB === -1) return a.localeCompare(b);
			if (indexA === -1) return 1;
			if (indexB === -1) return -1;
			return indexA - indexB;
		});
	};
	
	// Initialize expanded states - expand all by default when search is active, restore saved state when cleared
	const prevSearchTermRef = useRef('');
	const savedStateRef = useRef({ types: {}, times: {} });
	const currentExpandedTypesRef = useRef({});
	const currentExpandedTimesRef = useRef({});
	
	// Keep refs in sync with state
	useEffect(() => {
		currentExpandedTypesRef.current = expandedTypes;
	}, [expandedTypes]);
	
	useEffect(() => {
		currentExpandedTimesRef.current = expandedTimes;
	}, [expandedTimes]);
	
	useEffect(() => {
		const wasSearching = prevSearchTermRef.current.length > 0;
		const isSearching = searchTerm.length > 0;
		
		if (isSearching && !wasSearching) {
			// Transitioning from no search to search - save current state
			savedStateRef.current.types = { ...currentExpandedTypesRef.current };
			savedStateRef.current.times = { ...currentExpandedTimesRef.current };
			
			// Expand all matching categories for search
			const allTypes = Object.keys(filteredCategorized);
			const allTimes = {};
			allTypes.forEach(type => {
				allTimes[type] = Object.keys(filteredCategorized[type]);
			});
			setExpandedTypes(Object.fromEntries(allTypes.map(t => [t, true])));
			setExpandedTimes(Object.fromEntries(Object.entries(allTimes).flatMap(([type, times]) => 
				times.map(time => [`${type}-${time}`, true])
			)));
		} else if (isSearching && wasSearching) {
			// Still searching - update expanded to match filtered results
			const allTypes = Object.keys(filteredCategorized);
			const allTimes = {};
			allTypes.forEach(type => {
				allTimes[type] = Object.keys(filteredCategorized[type]);
			});
			setExpandedTypes(Object.fromEntries(allTypes.map(t => [t, true])));
			setExpandedTimes(Object.fromEntries(Object.entries(allTimes).flatMap(([type, times]) => 
				times.map(time => [`${type}-${time}`, true])
			)));
		} else if (!isSearching && wasSearching) {
			// Transitioning from search to no search - restore saved state
			setExpandedTypes(savedStateRef.current.types);
			setExpandedTimes(savedStateRef.current.times);
		}
		
		prevSearchTermRef.current = searchTerm;
	}, [searchTerm, filteredCategorized]);
	
	return (
		<div className="space-y-4">
			{/* Search Bar */}
			<div className="flex flex-row items-center gap-2 border border-[#363844] rounded-lg px-3 py-2 bg-[#17191d]">
				<img src={magnifying_glass} alt="search" className="w-4 h-4 opacity-70" />
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					placeholder="Search for a market"
					className="flex-1 bg-transparent border-none outline-none text-sm text-[#eff1f5] placeholder-gray-500"
				/>
			</div>
			
			{/* Categorized Options List */}
			<div className="max-h-96 overflow-y-auto space-y-2">
				{sortedTypes.map((type) => {
					const isTypeExpanded = expandedTypes[type] === true;
					const times = sortTimes(Object.keys(filteredCategorized[type]));
					
					return (
						<div key={type} className="border border-[#363844] rounded-lg overflow-hidden">
							{/* Type Header */}
							<div
								onClick={() => toggleType(type)}
								className="flex items-center justify-between p-3 cursor-pointer bg-[#1a1d24] hover:bg-[#22262e] transition-colors"
							>
								<div className="flex items-center gap-2">
									<svg 
										className={`w-4 h-4 text-gray-400 transition-transform ${isTypeExpanded ? 'rotate-90' : ''}`}
										fill="currentColor" 
										viewBox="0 0 20 20"
									>
										<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
									</svg>
									<span className="text-sm font-semibold text-gray-200">{type}</span>
									<span className="text-xs text-gray-500">
										({Object.values(filteredCategorized[type]).flat().length})
									</span>
								</div>
							</div>
							
							{/* Time Sections */}
							{isTypeExpanded && (
								<div className="bg-[#17191d]">
									{times.map((time) => {
										const typeTimeKey = `${type}-${time}`;
										const isTimeExpanded = expandedTimes[typeTimeKey] === true;
										const markets = filteredCategorized[type][time];
										
										return (
											<div key={typeTimeKey} className="border-t border-[#363844]">
												{/* Time Header */}
												<div
													onClick={() => toggleTime(typeTimeKey)}
													className="flex items-center justify-between px-4 py-2 cursor-pointer bg-[#17191d] hover:bg-[#1a1d24] transition-colors"
												>
													<div className="flex items-center gap-2">
														<svg 
															className={`w-3 h-3 text-gray-500 transition-transform ${isTimeExpanded ? 'rotate-90' : ''}`}
															fill="currentColor" 
															viewBox="0 0 20 20"
														>
															<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
														</svg>
														<span className="text-xs font-medium text-gray-300">{time}</span>
														<span className="text-xs text-gray-600">({markets.length})</span>
													</div>
												</div>
												
												{/* Market Items */}
												{isTimeExpanded && (
													<div className="pl-8 pr-2 pb-2">
														{markets.map((market) => (
															<div
																key={market.value}
																onClick={() => handleToggle(market)}
																className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-[#22262e] transition-colors"
															>
																<img 
																	src={isChecked(market) ? checked_checkbox : unchecked_checkbox} 
																	alt={isChecked(market) ? 'checked' : 'unchecked'}
																	className="w-4 h-4 flex-shrink-0"
																/>
																<span className={`text-xs ${isChecked(market) ? 'text-[#eff1f5] font-medium' : 'text-gray-400'}`}>
																	{market.label}
																</span>
															</div>
														))}
													</div>
												)}
											</div>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

// Sharp Books Modal Content Component - Direct checklist interface
const SharpBooksModalContent = ({ filters, index, setFilters, email, saveUserInfo, defaultStake }) => {
	const [searchTerm, setSearchTerm] = useState('');
	
	const allOptions = [...getWagerWizDisplayOptions(), ...SPORTSBOOKS];
	const displayValue = convertBackendToWagerWizDisplay(filters[index]?.sharpBooks || []);
	const filteredOptions = allOptions.filter(option => 
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleToggle = async (option) => {
		const copy = JSON.parse(JSON.stringify(filters));
		const currentDisplay = convertBackendToWagerWizDisplay(copy[index]?.sharpBooks || []);
		const isSelected = currentDisplay.some(item => item.value === option.value);
		
		let newDisplay;
		if (isSelected) {
			newDisplay = currentDisplay.filter(item => item.value !== option.value);
			// If all cleared, set to default
			if (newDisplay.length === 0) {
				newDisplay = [{ label: 'WagerWiz', value: 'WagerWiz' }];
			}
		} else {
			newDisplay = [...currentDisplay, option];
		}
		
		copy[index].sharpBooks = convertWagerWizSelectionToBackend(newDisplay);
		setFilters(copy);
		await saveUserInfo(copy, email, false, defaultStake);
	};


	const isChecked = (option) => {
		return displayValue.some(item => item.value === option.value);
	};

	return (
		<div className="space-y-4">
			{/* Search Bar */}
			<div className="flex flex-row items-center gap-2 border border-[#363844] rounded-lg px-3 py-2 bg-[#17191d]">
				<img src={magnifying_glass} alt="search" className="w-4 h-4 opacity-70" />
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					placeholder="Search for a sharp book"
					className="flex-1 bg-transparent border-none outline-none text-sm text-[#eff1f5] placeholder-gray-500"
				/>
			</div>

			{/* Options List */}
			<div className="max-h-96 overflow-y-auto space-y-2">
				{filteredOptions.map((option) => (
					<div
						key={option.value}
						onClick={() => handleToggle(option)}
						className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#22262e] transition-colors"
					>
						<img 
							src={isChecked(option) ? checked_checkbox : unchecked_checkbox} 
							alt={isChecked(option) ? 'checked' : 'unchecked'}
							className="w-5 h-5 flex-shrink-0"
						/>
						<span className={`text-sm ${isChecked(option) ? 'text-[#eff1f5] font-medium' : 'text-gray-400'}`}>
							{option.label}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

export const FiltersContainerArbitrage = (props) => {
	const { filters, setFilters, setIsFiltersOpen, email, isMobile, defaultStake } = props;
	const [saved, setSaved] = useState(false);
	const [whichFiltersOpen, setWhichFiltersOpen] = useState({ sports: false, leagues: false, sportsbooks: false, sharpBooks: false, markets: false, marketTypes: false });
	
	// Add state for property selection and editing
	const [isAddPropertyModalOpen, setIsAddPropertyModalOpen] = useState(false);
	const [filterView, setFilterView] = useState(null);
	const [activeRowIdx, setActiveRowIdx] = useState(null);
	const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);
	const [propertyToRemove, setPropertyToRemove] = useState(null);
	
	// Add ref for click outside functionality
	const editViewRef = useRef(null);

	// Store original filter values when modal opens (for reverting invalid changes)
	const originalFilterValuesRef = useRef(null);

	// Store original filter values when minMaxEv or minMaxOdds modal opens
	useEffect(() => {
		if ((filterView === 'minMaxEv' || filterView === 'minMaxOdds') && activeRowIdx !== null) {
			// Only store if we don't already have values for this row
			if (originalFilterValuesRef.current === null || originalFilterValuesRef.current.rowIdx !== activeRowIdx || originalFilterValuesRef.current.filterView !== filterView) {
				// Store the original values when modal first opens
				if (filterView === 'minMaxEv') {
					const originalMinMaxEv = filters[activeRowIdx]?.minMaxEv ? [...filters[activeRowIdx].minMaxEv] : [null, null];
					originalFilterValuesRef.current = {
						rowIdx: activeRowIdx,
						filterView: filterView,
						minMaxEv: originalMinMaxEv
					};
				} else if (filterView === 'minMaxOdds') {
					const originalMinMaxOdds = filters[activeRowIdx]?.minMaxOdds ? [...filters[activeRowIdx].minMaxOdds] : [null, null];
					originalFilterValuesRef.current = {
						rowIdx: activeRowIdx,
						filterView: filterView,
						minMaxOdds: originalMinMaxOdds
					};
				}
			}
		} else if ((filterView !== 'minMaxEv' && filterView !== 'minMaxOdds') || activeRowIdx === null) {
			// Clear the ref when modal closes
			originalFilterValuesRef.current = null;
		}
	}, [filterView, activeRowIdx, filters]);

	useEffect(() => {
		setTimeout(function () {
			setSaved(false);
		}, 5000);
	}, [saved]);

	// Handle clicks outside edit view
	useEffect(() => {
		const handleClickOutside = (event) => {
			// Check if click is within the edit view
			const isWithinEditView = editViewRef.current && editViewRef.current.contains(event.target);
			
			// Check if click is within autocomplete dropdown (Material-UI renders this in a portal)
			const autocompletePopper = document.querySelector('.MuiAutocomplete-popper');
			const isWithinAutocomplete = autocompletePopper && autocompletePopper.contains(event.target);
			
			// Check if click is on autocomplete-related elements
			const autocompleteRoot = document.querySelector('.MuiAutocomplete-root');
			const isWithinAutocompleteRoot = autocompleteRoot && autocompleteRoot.contains(event.target);
			
			// Check if click is on clear/select all buttons
			const clearAllButton = document.querySelector('.clear-all-button');
			const selectAllButton = document.querySelector('.select-all-button');
			const isWithinButtons = (clearAllButton && clearAllButton.contains(event.target)) || 
								   (selectAllButton && selectAllButton.contains(event.target));
			
			if (filterView && activeRowIdx !== null && 
				!isWithinEditView && 
				!isWithinAutocomplete && 
				!isWithinAutocompleteRoot && 
				!isWithinButtons) {
				// Check for error and revert if needed
				if ((filterView === 'minMaxEv' || filterView === 'minMaxOdds') && originalFilterValuesRef.current) {
					if (filterView === 'minMaxEv') {
						const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
						const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
						const hasError = minEv !== null && maxEv !== null && minEv >= maxEv;
						if (hasError) {
							// Revert to original values
							const copy = JSON.parse(JSON.stringify(filters));
							copy[activeRowIdx].minMaxEv = [...originalFilterValuesRef.current.minMaxEv];
							setFilters(copy);
						}
					} else if (filterView === 'minMaxOdds') {
						const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
						const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
						const hasRangeError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
						const hasBannedRangeError = (minOdds !== null && minOdds > -100 && minOdds < 100) || (maxOdds !== null && maxOdds > -100 && maxOdds < 100);
						const hasError = hasRangeError || hasBannedRangeError;
						if (hasError) {
							// Revert to original values
							const copy = JSON.parse(JSON.stringify(filters));
							copy[activeRowIdx].minMaxOdds = [...originalFilterValuesRef.current.minMaxOdds];
							setFilters(copy);
						}
					}
				}
				setFilterView(null);
				setActiveRowIdx(null);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [filterView, activeRowIdx, filters]);

	// Property to title mapping for arbitrage filters
	const propertyToTitleMapping = {
		sportsbooks: 'Sportsbooks',
		sharpBooks: 'Sharp Books',
		sports: 'Sports',
		leagues: 'Leagues',
		markets: 'Markets',
		marketTypes: 'Market Types',
		minMaxEv: 'Min/Max ROI%',
		minMaxOdds: 'Min/Max Odds',
		date: 'Date Range',
		include_only_selected_books: 'Only Include Selected Books',
		hide_alternates: 'Hide Alternate Lines',
	};

	// Helper function to check if a property has been changed from its default value
	const isPropertyChanged = (key, value) => {
		// dateDifference and default_stake are internal properties and should never be displayed as filter options
		if (key === 'dateDifference' || key === 'default_stake') {
			return false;
		}
		
		if (Array.isArray(value)) {
			if (key === 'minMaxEv') {
				// Check if both are sentinel values or null (default/Any)
				return !((value[0] === -9999999 || value[0] === null) && (value[1] === 9999999 || value[1] === null));
			}
			if (key === 'minMaxOdds') {
				// Check if both are sentinel values or null (default/Any)
				return !((value[0] === -9999999 || value[0] === null) && (value[1] === 9999999 || value[1] === null));
			}
			// For array properties like sportsbooks, sports, leagues, etc.
			return value.length > 0;
		}
		
		if (key === 'date') {
			return value !== null;
		}
		
		if (key === 'include_only_selected_books' || key === 'hide_alternates') {
			return value === true;
		}
		
		return false;
	};

	const mobileStyling = isMobile
		? {
				// width: 'min-content',
				// transform: 'scale(0.8)',
				// transformOrigin: 'top left',
				// height: '148vh',
		  }
		: {};

	return (
		<div>
			<div className="mt-5 rounded-2xl" style={{ backgroundColor: '#22262e', borderTopLeftRadius: '0px', borderTopRightRadius: '0px', ...mobileStyling }}>
				{/* Header Section */}
				<div className="px-4" style={{ paddingTop: '12px' }}>
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"></div>
							<h2 className="text-xl font-semibold text-white">Arbitrage Filters</h2>
						</div>
						<button
							onClick={() => setIsFiltersOpen(false)}
							className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200"
						>
							<img src={close_icon} alt="close icon" className="w-4 h-4" />
						</button>
					</div>
				</div>
				<div className="mt-3" style={{ height: '1px', width: '100%', backgroundColor: '#363844' }} />

				{/* Filter Properties Display */}
				{filters?.map((row, index) => {
					return (
						<div key={index} className="p-4">
							{/* Modified Properties Display */}
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
								{Object.entries(row).map(([key, value]) => {
									// Only show properties that have been changed from their default values
									if (!isPropertyChanged(key, value)) {
										return null;
									}
									
									let displayValue = null;
									let count = null;
									
									if (["sportsbooks", "sharpBooks", "sports", "leagues", "markets", "marketTypes"].includes(key)) {
										count = value?.length;
									}
									if (key === "minMaxEv" && Array.isArray(value)) {
										const min = (value[0] === null || value[0] === -9999999) ? "Any" : `${value[0]}%`;
										const max = (value[1] === null || value[1] === 9999999) ? "Any" : `${value[1]}%`;
										displayValue = `${min} / ${max}`;
									}
									if (key === "minMaxOdds" && Array.isArray(value)) {
										const min = (value[0] === null || value[0] === -9999999) ? "Any" : (value[0] > 0 ? `+${value[0]}` : value[0]);
										const max = (value[1] === null || value[1] === 9999999) ? "Any" : (value[1] > 0 ? `+${value[1]}` : value[1]);
										displayValue = `${min} / ${max}`;
									}
									if (key === "date" && value) {
										try {
											const dateObj = new Date(value);
											displayValue = isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleDateString('en-US');
										} catch {
											displayValue = '';
										}
									}
									if (key === "include_only_selected_books" || key === "hide_alternates") {
										displayValue = value ? 'On' : 'Off';
									}
									
									return (
										<div key={key} className="bg-gradient-to-br from-[#1a1d24] to-[#17191d] rounded-xl p-4 border border-[#363844] cursor-pointer hover:bg-gradient-to-br hover:from-[#2a2d34] hover:to-[#272a2f] transition-all duration-200 shadow-sm hover:shadow-md"
											onClick={() => {
												// For hide_alternates and include_only_selected_books, directly toggle it instead of opening modal
												if (key === 'hide_alternates' || key === 'include_only_selected_books') {
													const copy = JSON.parse(JSON.stringify(filters));
													if (key === 'hide_alternates') {
														copy[index].hide_alternates = !copy[index].hide_alternates;
													} else {
														copy[index].include_only_selected_books = !copy[index].include_only_selected_books;
													}
													setFilters(copy);
													saveUserInfoArbitrage(copy, email, false, defaultStake);
												} else {
													// Open the appropriate filter component for editing
													setActiveRowIdx(index);
													setFilterView(key);
													const _init = { sports: false, leagues: false, sportsbooks: false, sharpBooks: false, markets: false, marketTypes: false };
													if (Object.prototype.hasOwnProperty.call(_init, key)) {
														_init[key] = true;
														setWhichFiltersOpen(_init);
													}
												}
											}}
										>
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-3">
													<div className="w-2 h-2 bg-blue-500 rounded-full"></div>
													<span className="text-sm font-medium text-gray-200">{propertyToTitleMapping[key] || key}</span>
													{key === 'include_only_selected_books' && (
														<Tooltip 
															title="When enabled, arbitrage plays will only be shown if books from both sides of the arb are selected in the sportsbooks dropdown."
															arrow
															placement="top"
														>
															<HelpOutlineIcon className="text-gray-400 text-sm cursor-help" style={{ fontSize: '16px' }} />
														</Tooltip>
													)}
													{key === 'hide_alternates' && (
														<Tooltip 
															title="Only show primary/featured lines. Hiding alt lines can help you focus on more liquid markets and delay you from being profiled as a winning player."
															arrow
															placement="top"
														>
															<HelpOutlineIcon className="text-gray-400 text-sm cursor-help" style={{ fontSize: '16px' }} />
														</Tooltip>
													)}
												</div>
												<div className="flex items-center gap-3">
													{displayValue ? (
														<span className="bg-gradient-to-r from-blue-600 to-blue-700 text-xs px-3 py-1.5 rounded-full text-white font-medium shadow-sm">{displayValue}</span>
													) : count !== null ? (
														<span className="bg-gradient-to-r from-blue-600 to-blue-700 text-xs px-3 py-1.5 rounded-full text-white font-medium shadow-sm">{count}</span>
													) : null}
													<button
														onClick={(e) => {
															e.stopPropagation(); // Prevent triggering the card click
															// Open confirmation modal
															setPropertyToRemove({ rowIndex: index, propertyKey: key, propertyName: propertyToTitleMapping[key] });
															setIsConfirmRemoveModalOpen(true);
														}}
														className="w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors duration-200 shadow-sm hover:shadow-md"
														title="Remove this property"
													>
														<svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
														</svg>
													</button>
													<div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center">
														<svg className="w-3 h-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
														</svg>
													</div>
												</div>
											</div>
										</div>
									);
								})}
								
								{/* Add Property Button - Inline with other filter elements */}
								<button
									onClick={() => {
										setIsAddPropertyModalOpen(true);
									}}
									className="bg-gradient-to-br from-[#1a1d24] to-[#17191d] rounded-xl p-4 border border-[#363844] cursor-pointer hover:bg-gradient-to-br hover:from-[#2a2d34] hover:to-[#272a2f] transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-start gap-3"
								>
									<svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
									</svg>
									<span className="text-sm font-medium text-gray-200">Add Property</span>
								</button>
								
								{/* Show message if no properties are set */}
								{Object.entries(row).filter(([key, value]) => isPropertyChanged(key, value)).length === 0 && (
									<div className="col-span-full text-center py-12">
										<div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full flex items-center justify-center">
											<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
											</svg>
										</div>
										<p className="text-gray-400 font-medium mb-2">No properties set for arbitrage filters</p>
										<p className="text-sm text-gray-500">Click "Add Property" to get started</p>
									</div>
								)}
							</div>
						</div>
					);
				})}

				{/* Action Buttons Section */}
				<div className="flex justify-end px-6 pb-6">
					<div className="flex items-center gap-4">
						<button
							onClick={async (e) => {
								e.stopPropagation();

								const clearedFilters = [
									{
										sports: [],
										leagues: [],
										sportsbooks: [],
										sharpBooks: [],
										markets: [],
										marketTypes: [],
										minMaxEv: [-9999999, 9999999],
										minMaxOdds: [-9999999, 9999999],
										date: null,
										include_only_selected_books: false,
										hide_alternates: false,
									},
								];

								setFilters(clearedFilters);
								
								// Save the cleared filters to localStorage
								await saveUserInfoArbitrage(clearedFilters, email, false, defaultStake);
							}}
							className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-700/30 transition-all duration-200"
						>
							Clear All
						</button>
						<button
							onClick={async (e) => {
								e.stopPropagation();
								await saveUserInfoArbitrage(filters, email, false, defaultStake);
								setSaved(true);
							}}
							className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2"
						>
							Save Filters
						</button>
						{saved && <span className="text-sm" style={{ color: '#9ae6b4' }}>Saved!</span>}
					</div>
				</div>

				{/* Global Fullscreen Filter Edit Modal */}
				{filterView !== null && activeRowIdx !== null && (
					<div 
						className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] overflow-y-auto"
						onClick={() => {
							// Check for error and revert if needed
							if ((filterView === 'minMaxEv' || filterView === 'minMaxOdds') && originalFilterValuesRef.current) {
								if (filterView === 'minMaxEv') {
									const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
									const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
									const hasError = minEv !== null && maxEv !== null && minEv >= maxEv;
									if (hasError) {
										// Revert to original values
										const copy = JSON.parse(JSON.stringify(filters));
										copy[activeRowIdx].minMaxEv = [...originalFilterValuesRef.current.minMaxEv];
										setFilters(copy);
									}
								} else if (filterView === 'minMaxOdds') {
									const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
									const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
									const hasRangeError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
									const hasBannedRangeError = (minOdds !== null && minOdds > -100 && minOdds < 100) || (maxOdds !== null && maxOdds > -100 && maxOdds < 100);
									const hasError = hasRangeError || hasBannedRangeError;
									if (hasError) {
										// Revert to original values
										const copy = JSON.parse(JSON.stringify(filters));
										copy[activeRowIdx].minMaxOdds = [...originalFilterValuesRef.current.minMaxOdds];
										setFilters(copy);
									}
								}
							}
							setFilterView(null);
							setActiveRowIdx(null);
						}}
					>
						<div 
							className={`bg-gradient-to-br from-[#1a1d24] to-[#17191d] rounded-2xl border border-[#363844] shadow-2xl w-full mx-4 my-8 ${filterView === 'minMaxEv' || filterView === 'minMaxOdds' ? 'max-w-[340px]' : filterView === 'sharpBooks' || filterView === 'sportsbooks' || filterView === 'markets' || filterView === 'marketTypes' || filterView === 'sports' || filterView === 'leagues' || filterView === 'date' ? 'max-w-sm' : 'max-w-3xl'}`}
							onClick={(e) => e.stopPropagation()}
							ref={editViewRef}
						>
							<div className={filterView === 'sharpBooks' || filterView === 'sportsbooks' || filterView === 'markets' || filterView === 'marketTypes' || filterView === 'sports' || filterView === 'leagues' || filterView === 'date' || filterView === 'minMaxEv' || filterView === 'minMaxOdds' ? 'p-5' : 'p-6'}>
								<div className="flex justify-between items-center mb-5">
									<h4 className={`font-medium text-gray-200 ${filterView === 'sharpBooks' || filterView === 'sportsbooks' || filterView === 'markets' || filterView === 'marketTypes' || filterView === 'sports' || filterView === 'leagues' || filterView === 'date' || filterView === 'minMaxEv' || filterView === 'minMaxOdds' ? 'text-lg' : 'text-base'}`}>
										{filterView === 'date' ? 'Select End Date' : filterView === 'sharpBooks' || filterView === 'sportsbooks' || filterView === 'markets' || filterView === 'marketTypes' || filterView === 'sports' || filterView === 'leagues' || filterView === 'minMaxEv' || filterView === 'minMaxOdds' ? propertyToTitleMapping[filterView] || filterView : `Edit ${propertyToTitleMapping[filterView] || filterView}`}
									</h4>
									<button
										onClick={() => {
											// Check for error and revert if needed
											if ((filterView === 'minMaxEv' || filterView === 'minMaxOdds') && originalFilterValuesRef.current) {
												if (filterView === 'minMaxEv') {
													const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
													const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
													const hasError = minEv !== null && maxEv !== null && minEv >= maxEv;
													if (hasError) {
														// Revert to original values
														const copy = JSON.parse(JSON.stringify(filters));
														copy[activeRowIdx].minMaxEv = [...originalFilterValuesRef.current.minMaxEv];
														setFilters(copy);
													}
												} else if (filterView === 'minMaxOdds') {
													const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
													const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
													const hasRangeError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
													const hasBannedRangeError = (minOdds !== null && minOdds > -100 && minOdds < 100) || (maxOdds !== null && maxOdds > -100 && maxOdds < 100);
													const hasError = hasRangeError || hasBannedRangeError;
													if (hasError) {
														// Revert to original values
														const copy = JSON.parse(JSON.stringify(filters));
														copy[activeRowIdx].minMaxOdds = [...originalFilterValuesRef.current.minMaxOdds];
														setFilters(copy);
													}
												}
											}
											setFilterView(null);
											setActiveRowIdx(null);
										}}
										className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200"
									>
										<svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
											<path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
										</svg>
									</button>
								</div>

								<div>
									{filterView === 'sportsbooks' && (
										<SportsbooksModalContent
											filters={filters}
											index={activeRowIdx}
											setFilters={setFilters}
											email={email}
											saveUserInfo={saveUserInfoArbitrage}
											defaultStake={defaultStake}
										/>
									)}

									{filterView === 'sharpBooks' && (
										<SharpBooksModalContent
											filters={filters}
											index={activeRowIdx}
											setFilters={setFilters}
											email={email}
											saveUserInfo={saveUserInfoArbitrage}
											defaultStake={defaultStake}
										/>
									)}

									{filterView === 'sports' && (
										<SportsModalContent
											filters={filters}
											index={activeRowIdx}
											setFilters={setFilters}
											email={email}
											saveUserInfo={saveUserInfoArbitrage}
											defaultStake={defaultStake}
										/>
									)}

									{filterView === 'leagues' && (
										<LeaguesModalContent
											filters={filters}
											index={activeRowIdx}
											setFilters={setFilters}
											email={email}
											saveUserInfo={saveUserInfoArbitrage}
											defaultStake={defaultStake}
										/>
									)}

									{filterView === 'markets' && (
										<MarketsModalContent
											filters={filters}
											index={activeRowIdx}
											setFilters={setFilters}
											email={email}
											saveUserInfo={saveUserInfoArbitrage}
											defaultStake={defaultStake}
										/>
									)}

									{filterView === 'marketTypes' && (
										<MarketTypesModalContent
											filters={filters}
											index={activeRowIdx}
											setFilters={setFilters}
											email={email}
											saveUserInfo={saveUserInfoArbitrage}
											defaultStake={defaultStake}
										/>
									)}

									{filterView === 'minMaxEv' && (() => {
										const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
										const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
										const hasError = minEv !== null && maxEv !== null && minEv >= maxEv;
										
										return (
											<div className="space-y-4">
												<p className="text-sm text-gray-400">
													Set minimum and maximum ROI percentages for your results.
												</p>
												<div className="flex items-center gap-3">
													<div className="relative w-fit">
														<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">Min</span>
														<input
															type="text"
															step="0.1"
															value={minEv === null || minEv === -9999999 ? '' : minEv === -9999998 ? '-' : minEv !== undefined ? String(minEv) : ''}
															onChange={(e) => {
																let inputValue = e.target.value;
																// Block scientific notation (e, E, +)
																if (/[eE+]/.test(inputValue)) {
																	return;
																}
																// Allow negative sign only at the start
																if (inputValue.includes('-') && inputValue.indexOf('-') !== 0) {
																	inputValue = inputValue.replace(/-/g, '');
																}
																const copy = JSON.parse(JSON.stringify(filters));
																const currentMax = copy[activeRowIdx]?.minMaxEv?.[1] ?? null;
																if (inputValue === '') {
																	copy[activeRowIdx].minMaxEv = [-9999999, currentMax];
																	setFilters(copy);
																	return;
																}
																// Limit to 1 decimal place
																const numValue = parseFloat(inputValue);
																if (!isNaN(numValue)) {
																	const roundedValue = Math.round(numValue * 10) / 10;
																	copy[activeRowIdx].minMaxEv = [roundedValue, currentMax];
																	setFilters(copy);
																	// Don't save invalid values - only save when user clicks Save and Exit
																} else if (inputValue === '-') {
																	// Allow just '-' to be displayed while user is typing
																	// Store as a temporary placeholder
																	copy[activeRowIdx].minMaxEv = [-9999998, currentMax];
																	setFilters(copy);
																}
															}}
															onKeyDown={(e) => {
																// Block e, E, + keys
																// Allow - only at the start (when selectionStart is 0 or input is empty)
																if (e.key === 'e' || e.key === 'E' || e.key === '+') {
																	e.preventDefault();
																} else if (e.key === '-') {
																	// Allow - if cursor is at start or input is empty
																	const input = e.target;
																	if (input.selectionStart !== 0 && input.value !== '') {
																		e.preventDefault();
																	}
																}
															}}
															placeholder="Any"
															className={`w-36 pl-11 pr-8 py-3 bg-[#17191d] border rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0b63f4] focus:border-transparent transition-all ${hasError ? 'border-red-500' : 'border-[#363844]'}`}
														/>
														<span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">%</span>
													</div>
													<div className="relative w-fit">
														<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">Max</span>
														<input
															type="text"
															step="0.1"
															value={maxEv === null || maxEv === 9999999 ? '' : maxEv === 9999998 ? '-' : maxEv !== undefined ? String(maxEv) : ''}
															onChange={(e) => {
																let inputValue = e.target.value;
																// Block scientific notation (e, E, +)
																if (/[eE+]/.test(inputValue)) {
																	return;
																}
																// Allow negative sign only at the start
																if (inputValue.includes('-') && inputValue.indexOf('-') !== 0) {
																	inputValue = inputValue.replace(/-/g, '');
																}
																const copy = JSON.parse(JSON.stringify(filters));
																const currentMin = copy[activeRowIdx]?.minMaxEv?.[0] ?? null;
																if (inputValue === '') {
																	copy[activeRowIdx].minMaxEv = [currentMin, 9999999];
																	setFilters(copy);
																	return;
																}
																// Limit to 1 decimal place
																const numValue = parseFloat(inputValue);
																if (!isNaN(numValue)) {
																	const roundedValue = Math.round(numValue * 10) / 10;
																	copy[activeRowIdx].minMaxEv = [currentMin, roundedValue];
																	setFilters(copy);
																	// Don't save invalid values - only save when user clicks Save and Exit
																} else if (inputValue === '-') {
																	// Allow just '-' to be displayed while user is typing
																	// Store as a temporary placeholder
																	copy[activeRowIdx].minMaxEv = [currentMin, 9999998];
																	setFilters(copy);
																}
															}}
															onKeyDown={(e) => {
																// Block e, E, + keys
																// Allow - only at the start (when selectionStart is 0 or input is empty)
																if (e.key === 'e' || e.key === 'E' || e.key === '+') {
																	e.preventDefault();
																} else if (e.key === '-') {
																	// Allow - if cursor is at start or input is empty
																	const input = e.target;
																	if (input.selectionStart !== 0 && input.value !== '') {
																		e.preventDefault();
																	}
																}
															}}
															placeholder="Any"
															className={`w-36 pl-11 pr-8 py-3 bg-[#17191d] border rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0b63f4] focus:border-transparent transition-all ${hasError ? 'border-red-500' : 'border-[#363844]'}`}
														/>
														<span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">%</span>
													</div>
												</div>
												{hasError && (
													<p className="text-xs text-red-500 leading-relaxed">
														Minimum value must be less than maximum value.
													</p>
												)}
												{!hasError && (
													<p className="text-xs text-gray-500 leading-relaxed">
														Leave blank to allow any value.
													</p>
												)}
											</div>
										);
									})()}

									{filterView === 'minMaxOdds' && (() => {
										const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
										const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
										const hasRangeError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
										const hasBannedRangeError = (minOdds !== null && minOdds > -100 && minOdds < 100) || (maxOdds !== null && maxOdds > -100 && maxOdds < 100);
										const hasError = hasRangeError || hasBannedRangeError;
										
										return (
											<div className="space-y-4">
												<p className="text-sm text-gray-400">
													Set minimum and maximum odds for your results.
												</p>
												<div className="flex items-center gap-3">
													<div className="relative w-fit">
														<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">Min</span>
														<input
															type="text"
															value={minOdds === null || minOdds === -9999999 ? '' : minOdds === -9999998 ? '-' : minOdds !== undefined ? String(minOdds) : ''}
															onChange={(e) => {
																let inputValue = e.target.value;
																// Block scientific notation (e, E, +)
																if (/[eE+]/.test(inputValue)) {
																	return;
																}
																// Allow negative sign only at the start
																if (inputValue.includes('-') && inputValue.indexOf('-') !== 0) {
																	inputValue = inputValue.replace(/-/g, '');
																}
																const copy = JSON.parse(JSON.stringify(filters));
																const currentMax = copy[activeRowIdx]?.minMaxOdds?.[1] ?? null;
																if (inputValue === '') {
																	copy[activeRowIdx].minMaxOdds = [-9999999, currentMax];
																	setFilters(copy);
																	return;
																}
																// Parse as integer (no decimals)
																const numValue = parseFloat(inputValue);
																if (!isNaN(numValue)) {
																	// Round to integer (no decimals)
																	const roundedValue = Math.round(numValue);
																	copy[activeRowIdx].minMaxOdds = [roundedValue, currentMax];
																	setFilters(copy);
																	// Don't save invalid values - only save when user clicks Save and Exit
																} else if (inputValue === '-') {
																	// Allow just '-' to be displayed while user is typing
																	// Store as a temporary placeholder
																	copy[activeRowIdx].minMaxOdds = [-9999998, currentMax];
																	setFilters(copy);
																}
															}}
															onKeyDown={(e) => {
																// Block e, E, + keys
																// Allow - only at the start (when selectionStart is 0 or input is empty)
																if (e.key === 'e' || e.key === 'E' || e.key === '+') {
																	e.preventDefault();
																} else if (e.key === '-') {
																	// Allow - if cursor is at start or input is empty
																	const input = e.target;
																	if (input.selectionStart !== 0 && input.value !== '') {
																		e.preventDefault();
																	}
																}
															}}
															placeholder="Any"
															className={`w-36 pl-11 pr-8 py-3 bg-[#17191d] border rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0b63f4] focus:border-transparent transition-all ${hasError ? 'border-red-500' : 'border-[#363844]'}`}
														/>
													</div>
													<div className="relative w-fit">
														<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">Max</span>
														<input
															type="text"
															value={maxOdds === null || maxOdds === 9999999 ? '' : maxOdds === 9999998 ? '-' : maxOdds !== undefined ? String(maxOdds) : ''}
															onChange={(e) => {
																let inputValue = e.target.value;
																// Block scientific notation (e, E, +)
																if (/[eE+]/.test(inputValue)) {
																	return;
																}
																// Allow negative sign only at the start
																if (inputValue.includes('-') && inputValue.indexOf('-') !== 0) {
																	inputValue = inputValue.replace(/-/g, '');
																}
																const copy = JSON.parse(JSON.stringify(filters));
																const currentMin = copy[activeRowIdx]?.minMaxOdds?.[0] ?? null;
																if (inputValue === '') {
																	copy[activeRowIdx].minMaxOdds = [currentMin, 9999999];
																	setFilters(copy);
																	return;
																}
																// Parse as integer (no decimals)
																const numValue = parseFloat(inputValue);
																if (!isNaN(numValue)) {
																	// Round to integer (no decimals)
																	const roundedValue = Math.round(numValue);
																	copy[activeRowIdx].minMaxOdds = [currentMin, roundedValue];
																	setFilters(copy);
																	// Don't save invalid values - only save when user clicks Save and Exit
																} else if (inputValue === '-') {
																	// Allow just '-' to be displayed while user is typing
																	// Store as a temporary placeholder
																	copy[activeRowIdx].minMaxOdds = [currentMin, 9999998];
																	setFilters(copy);
																}
															}}
															onKeyDown={(e) => {
																// Block e, E, + keys
																// Allow - only at the start (when selectionStart is 0 or input is empty)
																if (e.key === 'e' || e.key === 'E' || e.key === '+') {
																	e.preventDefault();
																} else if (e.key === '-') {
																	// Allow - if cursor is at start or input is empty
																	const input = e.target;
																	if (input.selectionStart !== 0 && input.value !== '') {
																		e.preventDefault();
																	}
																}
															}}
															placeholder="Any"
															className={`w-36 pl-11 pr-8 py-3 bg-[#17191d] border rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0b63f4] focus:border-transparent transition-all ${hasError ? 'border-red-500' : 'border-[#363844]'}`}
														/>
													</div>
												</div>
												{hasError && (
													<p className="text-xs text-red-500 leading-relaxed">
														{hasRangeError && hasBannedRangeError 
															? 'Minimum value must be less than maximum value. Values between -100 and +100 are not allowed.'
															: hasRangeError 
																? 'Minimum value must be less than maximum value.'
																: 'Values between -100 and +100 are not allowed.'}
													</p>
												)}
												{!hasError && (
													<p className="text-xs text-gray-500 leading-relaxed">
														Leave blank to allow any value.
													</p>
												)}
											</div>
										);
									})()}

									{filterView === 'date' && (
										<DateModalContent
											filters={filters}
											index={activeRowIdx}
											setFilters={setFilters}
											email={email}
											saveUserInfo={saveUserInfoArbitrage}
											defaultStake={defaultStake}
											key={filters?.[activeRowIdx]?.dateDifference || 0}
										/>
									)}

								</div>

								{/* Select All, Clear All, and Save and Exit Buttons */}
								{(filterView === 'sportsbooks' || filterView === 'sharpBooks' || filterView === 'sports' || filterView === 'leagues' || filterView === 'markets' || filterView === 'marketTypes' || filterView === 'minMaxEv' || filterView === 'minMaxOdds' || filterView === 'date') && (
									<div className="mt-6 pt-5 border-t border-[#363844] flex justify-between items-center gap-3">
										{(filterView === 'sportsbooks' || filterView === 'sharpBooks' || filterView === 'sports' || filterView === 'leagues' || filterView === 'markets' || filterView === 'marketTypes') && (
											<div className="flex gap-2 flex-shrink-0">
												<button
													onClick={async () => {
														const copy = JSON.parse(JSON.stringify(filters));
														if (filterView === 'sportsbooks') {
															copy[activeRowIdx].sportsbooks = SPORTSBOOKS;
														} else if (filterView === 'sharpBooks') {
															copy[activeRowIdx].sharpBooks = convertWagerWizSelectionToBackend([...getWagerWizDisplayOptions(), ...SPORTSBOOKS]);
														} else if (filterView === 'sports') {
															copy[activeRowIdx].sports = SPORTS;
														} else if (filterView === 'leagues') {
															copy[activeRowIdx].leagues = LEAGUES;
														} else if (filterView === 'markets') {
															copy[activeRowIdx].markets = MARKETS.map((m) => ({ label: m, value: m }));
														} else if (filterView === 'marketTypes') {
															copy[activeRowIdx].marketTypes = SPORTS.map((s) => ({ label: s.label, value: s.value }));
														}
														setFilters(copy);
														await saveUserInfoArbitrage(copy, email, false, defaultStake);
													}}
													className="px-4 py-2 text-sm font-medium rounded-lg border border-[#0b63f4] bg-[rgba(11,99,244,0.1)] text-[#0b63f4] hover:bg-[rgba(11,99,244,0.2)] transition-colors"
												>
													Select All
												</button>
												<button
													onClick={async () => {
														const copy = JSON.parse(JSON.stringify(filters));
														if (filterView === 'sportsbooks') {
															copy[activeRowIdx].sportsbooks = [];
														} else if (filterView === 'sharpBooks') {
															copy[activeRowIdx].sharpBooks = [{ label: 'WagerWiz Mains', value: 'WagerWiz Mains' }, { label: 'WagerWiz Props', value: 'WagerWiz Props' }];
														} else if (filterView === 'sports') {
															copy[activeRowIdx].sports = [];
														} else if (filterView === 'leagues') {
															copy[activeRowIdx].leagues = [];
														} else if (filterView === 'markets') {
															copy[activeRowIdx].markets = [];
														} else if (filterView === 'marketTypes') {
															copy[activeRowIdx].marketTypes = [];
														}
														setFilters(copy);
														await saveUserInfoArbitrage(copy, email, false, defaultStake);
													}}
													className="px-4 py-2 text-sm font-medium rounded-lg border border-[#ef4444] bg-[rgba(239,68,68,0.1)] text-[#ef4444] hover:bg-[rgba(239,68,68,0.2)] transition-colors"
												>
													Clear All
												</button>
											</div>
										)}
										{filterView === 'date' && (
											<div className="flex gap-2 flex-shrink-0">
												<button
													onClick={async () => {
														const copy = JSON.parse(JSON.stringify(filters));
														const today = new Date();
														today.setHours(0, 0, 0, 0);
														copy[activeRowIdx].date = today.toString();
														copy[activeRowIdx].dateDifference = 0;
														setFilters(copy);
														await saveUserInfoArbitrage(copy, email, false, defaultStake);
													}}
													className="px-4 py-2 text-sm font-medium rounded-lg border border-[#0b63f4] bg-[rgba(11,99,244,0.1)] text-[#0b63f4] hover:bg-[rgba(11,99,244,0.2)] transition-colors"
												>
													Set to Today
												</button>
											</div>
										)}
										{filterView === 'minMaxEv' && (() => {
											const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
											const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
											const hasError = minEv !== null && maxEv !== null && minEv >= maxEv;
											
											return (
												<div className="flex gap-2 flex-shrink-0">
													<button
														onClick={async () => {
															const copy = JSON.parse(JSON.stringify(filters));
															copy[activeRowIdx].minMaxEv = [-9999999, 9999999];
															setFilters(copy);
															await saveUserInfoArbitrage(copy, email, false, defaultStake);
														}}
														className="px-4 text-sm font-medium rounded-lg border border-[#0b63f4] bg-[rgba(11,99,244,0.1)] text-[#0b63f4] hover:bg-[rgba(11,99,244,0.2)] transition-colors"
														style={{ paddingTop: '9px', paddingBottom: '9px' }}
													>
														Reset
													</button>
												</div>
											);
										})()}
										{filterView === 'minMaxOdds' && (() => {
											const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
											const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
											const hasError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
											
											return (
												<div className="flex gap-2 flex-shrink-0">
													<button
														onClick={async () => {
															const copy = JSON.parse(JSON.stringify(filters));
															copy[activeRowIdx].minMaxOdds = [-9999999, 9999999];
															setFilters(copy);
															await saveUserInfoArbitrage(copy, email, false, defaultStake);
														}}
														className="px-4 text-sm font-medium rounded-lg border border-[#0b63f4] bg-[rgba(11,99,244,0.1)] text-[#0b63f4] hover:bg-[rgba(11,99,244,0.2)] transition-colors"
														style={{ paddingTop: '9px', paddingBottom: '9px' }}
													>
														Reset
													</button>
												</div>
											);
										})()}
										<button
											onClick={async () => {
												// Check for error before saving
												if (filterView === 'minMaxEv') {
													const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
													const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
													const hasError = minEv !== null && maxEv !== null && minEv >= maxEv;
													if (hasError) {
														return; // Don't save if there's an error
													}
												} else if (filterView === 'minMaxOdds') {
													const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
													const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
													const hasRangeError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
													const hasBannedRangeError = (minOdds !== null && minOdds > -100 && minOdds < 100) || (maxOdds !== null && maxOdds > -100 && maxOdds < 100);
													const hasError = hasRangeError || hasBannedRangeError;
													if (hasError) {
														return; // Don't save if there's an error
													}
												}
												await saveUserInfoArbitrage(filters, email, false, defaultStake);
												setFilterView(null);
												setActiveRowIdx(null);
											}}
											disabled={(filterView === 'minMaxEv' || filterView === 'minMaxOdds') && (() => {
												if (filterView === 'minMaxEv') {
													const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
													const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
													return minEv !== null && maxEv !== null && minEv >= maxEv;
												} else if (filterView === 'minMaxOdds') {
													const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
													const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
													const hasRangeError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
													const hasBannedRangeError = (minOdds !== null && minOdds > -100 && minOdds < 100) || (maxOdds !== null && maxOdds > -100 && maxOdds < 100);
													return hasRangeError || hasBannedRangeError;
												}
												return false;
											})()}
											className={`px-4 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm hover:shadow-md transition-all duration-200 flex-shrink-0 ${(filterView === 'minMaxEv' || filterView === 'minMaxOdds') && (() => {
												if (filterView === 'minMaxEv') {
													const minEv = filters[activeRowIdx]?.minMaxEv?.[0];
													const maxEv = filters[activeRowIdx]?.minMaxEv?.[1];
													const hasError = minEv !== null && maxEv !== null && minEv >= maxEv;
													return hasError ? 'opacity-50 cursor-not-allowed' : '';
												} else if (filterView === 'minMaxOdds') {
													const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0];
													const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1];
													const hasRangeError = minOdds !== null && maxOdds !== null && minOdds >= maxOdds;
													const hasBannedRangeError = (minOdds !== null && minOdds > -100 && minOdds < 100) || (maxOdds !== null && maxOdds > -100 && maxOdds < 100);
													const hasError = hasRangeError || hasBannedRangeError;
													return hasError ? 'opacity-50 cursor-not-allowed' : '';
												}
												return '';
											})()}`}
											style={{ paddingTop: '9px', paddingBottom: '9px' }}
										>
											Save and Exit
										</button>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Property Selection Modal */}
				{isAddPropertyModalOpen && (
					<div 
						className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" 
						style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: '115vh' }}
						onClick={() => setIsAddPropertyModalOpen(false)}
					>
						<div 
							className="bg-gradient-to-br from-[#1a1d24] to-[#17191d] rounded-2xl border border-[#363844] shadow-2xl max-w-4xl w-full mx-4"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="p-6">
								<div className="flex justify-between items-center mb-6">
									<div className="flex items-center gap-3">
										<div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"></div>
										<h3 className="text-xl font-semibold text-white">Add Property to Arbitrage Filters</h3>
									</div>
									<button
										onClick={() => setIsAddPropertyModalOpen(false)}
										className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200"
									>
										<svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
											<path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
										</svg>
									</button>
								</div>
								
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
									{Object.entries(propertyToTitleMapping)
										.filter(([key]) => {
											// Only show if not already set in this row (or set to default values)
											const value = filters[0][key];
											
											// For minMaxEv, check if it's at default (sentinel values or null)
											if (key === 'minMaxEv') {
												return !value || !Array.isArray(value) || ((value[0] === -9999999 || value[0] === null) && (value[1] === 9999999 || value[1] === null));
											}
											
											// For minMaxOdds, check if it's at default (sentinel values or null)
											if (key === 'minMaxOdds') {
												return !value || !Array.isArray(value) || ((value[0] === -9999999 || value[0] === null) && (value[1] === 9999999 || value[1] === null));
											}
											
											// For other properties
											return !value ||
												(Array.isArray(value) && value.length === 0) ||
												(typeof value !== 'object' && !value);
										})
										.map(([key, label]) => (
											<button
												key={key}
												onClick={async () => {
													// For hide_alternates and include_only_selected_books, directly toggle it instead of opening modal
													if (key === 'hide_alternates' || key === 'include_only_selected_books') {
														const copy = JSON.parse(JSON.stringify(filters));
														if (key === 'hide_alternates') {
															copy[0].hide_alternates = !copy[0].hide_alternates;
														} else {
															copy[0].include_only_selected_books = !copy[0].include_only_selected_books;
														}
														setFilters(copy);
														await saveUserInfoArbitrage(copy, email, false, defaultStake);
														setIsAddPropertyModalOpen(false);
													} else {
														setActiveRowIdx(0);
														setFilterView(key);
														setIsAddPropertyModalOpen(false);
													}
												}}
												className="p-4 rounded-xl bg-gradient-to-br from-[#1a1d24] to-[#17191d] border border-[#363844] hover:bg-gradient-to-br hover:from-[#2a2d34] hover:to-[#272a2f] text-white transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-between group"
											>
												<span className="font-medium">{label}</span>
												<div className="w-5 h-5 rounded-full bg-gray-600 group-hover:bg-gray-500 flex items-center justify-center transition-colors duration-200">
													<svg className="w-3 h-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
														<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
													</svg>
												</div>
											</button>
										))}
								</div>
								
								{/* Cancel button */}
								<div className="flex justify-end pt-6 border-t border-[#363844]">
									<button
										onClick={() => setIsAddPropertyModalOpen(false)}
										className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Confirmation Modal for Removing Property */}
				{isConfirmRemoveModalOpen && propertyToRemove && (
					<div 
						className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" 
						style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: '115vh' }}
						onClick={() => {
							setIsConfirmRemoveModalOpen(false);
							setPropertyToRemove(null);
						}}
					>
						<div 
							className="bg-gradient-to-br from-[#1a1d24] to-[#17191d] rounded-2xl border border-[#363844] shadow-2xl max-w-md w-full mx-4"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="p-6">
								<div className="flex justify-between items-center mb-6">
									<div className="flex items-center gap-3">
										<div className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-600 rounded-full"></div>
										<h3 className="text-xl font-semibold text-white">Remove Property</h3>
									</div>
									<button
										onClick={() => {
											setIsConfirmRemoveModalOpen(false);
											setPropertyToRemove(null);
										}}
										className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200"
									>
										<svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
											<path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
										</svg>
									</button>
								</div>
								
								<div className="text-center mb-6">
									<div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
										<svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
										</svg>
									</div>
									<p className="text-gray-300 font-medium mb-2">
										Are you sure you want to remove <span className="text-white font-semibold">{propertyToRemove.propertyName}</span>?
									</p>
								</div>
								
								<div className="flex justify-between items-center pt-6 border-t border-[#363844]">
									<button
										onClick={() => {
											setIsConfirmRemoveModalOpen(false);
											setPropertyToRemove(null);
										}}
										className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-700/30 transition-all duration-200 flex items-center gap-2"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
										</svg>
										Keep Property
									</button>
									<button
										onClick={async () => {
											// Reset property to default
											const copy = JSON.parse(JSON.stringify(filters));
											const { rowIndex, propertyKey } = propertyToRemove;
											
											if (['sportsbooks', 'sharpBooks', 'sports', 'leagues', 'markets', 'marketTypes'].includes(propertyKey)) {
												copy[rowIndex][propertyKey] = [];
											} else if (propertyKey === 'minMaxEv') {
												copy[rowIndex][propertyKey] = [null, null];
											} else if (propertyKey === 'minMaxOdds') {
												copy[rowIndex][propertyKey] = [null, null];
											} else if (propertyKey === 'date') {
												copy[rowIndex][propertyKey] = null;
											} else if (propertyKey === 'include_only_selected_books' || propertyKey === 'hide_alternates') {
												copy[rowIndex][propertyKey] = false;
											}
											
											setFilters(copy);
											await saveUserInfoArbitrage(copy, email, false, defaultStake);
											setIsConfirmRemoveModalOpen(false);
											setPropertyToRemove(null);
										}}
										className="px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-3"
									>
										<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
										</svg>
										Remove Property
									</button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
